import baseConfig from "../config/baseConfig.js"

/**
 * API Response interface for standardized responses
 */
export interface ApiResponse<T = any> {
    success: boolean;
    data?: T;
    message?: string;
    status: number;
    error?: string;
}

/**
 * Helper function to get a cookie value by name
 */
const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop()?.split(';').shift() || null;
    }
    return null;
}

/**
 * API Request configuration interface
 */
export interface ApiRequestConfig {
    endpoint: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    data?: any;
    headers?: Record<string, string>;
    credentials?: RequestCredentials;
}

/**
 * Generic API request handler
 * Handles requests, responses, and errors in a centralized way
 * 
 * @param config - API request configuration
 * @returns Promise with standardized ApiResponse
 */
export const apiRequest = async <T = any>(
    config: ApiRequestConfig
): Promise<ApiResponse<T>> => {
    const {
        endpoint,
        method = 'GET',
        data = null,
        headers = {},
        credentials = 'include'
    } = config;

    try {
        // Build request options
        const requestOptions: RequestInit = {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...headers
            },
            credentials
        };

        // Add body for POST, PUT, PATCH requests
        if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
            requestOptions.body = JSON.stringify(data);
        }

        // Make the request
        const response = await fetch(baseConfig.backendUrl + endpoint, requestOptions);
        
        // Parse response
        let responseData: any = null;
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
            responseData = await response.json();
        } else {
            responseData = await response.text();
        }

        // Handle response based on status
        if (response.ok) {
            return {
                success: true,
                data: responseData,
                message: responseData?.message || 'Request successful',
                status: response.status
            };
        } else {
            // Handle error responses
            return {
                success: false,
                message: responseData?.message || `Request failed with status ${response.status}`,
                error: responseData?.error || response.statusText,
                status: response.status
            };
        }
    } catch (error) {
        // Handle network errors
        console.error('API Request Error:', error);
        return {
            success: false,
            message: 'Network error: Unable to connect to server',
            error: error instanceof Error ? error.message : 'Unknown error',
            status: 0
        };
    }
};

/**
 * Fetch CSRF token from backend
 * @returns Promise with ApiResponse containing the CSRF token
 */
export const getCsrfToken = async (): Promise<ApiResponse<{ token: string }>> => {
    const endpoint = `${baseConfig.backendServiceApiBase}/csrf_token`;
    
    return apiRequest<{ token: string }>({
        endpoint,
        method: 'GET',
        credentials: 'include'
    });
};

/**
 * Submit registration form data
 * @param csrfToken - CSRF token for security
 * @param formData - Form data to submit
 * @returns Promise with ApiResponse
 */
/**
 * Fetch the number of taken and available places for the additional event
 * @returns Promise with ApiResponse containing taken, max, and available counts
 */
export const getAdditionalEventPlaces = async (): Promise<ApiResponse<{ taken: number; max: number; available: number }>> => {
    const endpoint = `${baseConfig.backendServiceApiBase}/registration_data/additional_event_places`;
    return apiRequest<{ taken: number; max: number; available: number }>({
        endpoint,
        method: 'GET',
    });
};

export const submitRegistration = async (
    csrfToken: string,
    formData: object
): Promise<ApiResponse> => {
    const endpoint = `${baseConfig.backendServiceApiBase}/email/send_email`;

    // Debug: Check if cookie is present
    const csrfCookie = getCookie('csrf_token');
    if (csrfCookie) {
        console.log('CSRF cookie found:', csrfCookie.substring(0, 10) + '...');
    } else {
        console.log('CSRF cookie not found (expected in dev due to SameSite restrictions)');
    }

    return apiRequest({
        endpoint,
        method: 'POST',
        data: formData,
        headers: {
            'X-CSRF-Token': csrfToken
        },
        credentials: 'include'
    });
};